/**
 * chain-eos/mechat/attachments.ts — NFT attachments inside encrypted messages.
 *
 * There is no file storage anywhere in this protocol, so an "image" in a chat is
 * a reference to an AtomicAssets asset the sender owns: the message text carries
 * `nft:<asset_id>` and the recipient resolves the media URL from AtomicAssets
 * (usually IPFS). Nothing is uploaded, nothing is hosted by us.
 *
 * Consequences worth remembering:
 *  - Attaching does NOT transfer the NFT. The sender keeps it; this is a picture,
 *    not a gift.
 *  - The marker lives inside the ciphertext, so the chain shows nothing but the
 *    message size — but the recipient's device does hit an IPFS gateway to render,
 *    which reveals to that gateway that someone is viewing the asset.
 *  - Only asset ids are accepted, never raw IPFS hashes or URLs. Accepting those
 *    would turn a private messenger into an image host with everything that
 *    implies for moderation.
 *  - Wallets that do not know this convention (PayCash) simply show the literal
 *    text `nft:1099...`, which is ugly but not broken.
 *
 * The PayCash app's own chat has a second, unrelated attachment convention for
 * *files* it uploads to its own server (`files.paycash.online`): the message body
 * carries a literal `<file>url;fileName;fileSize;fileExt</file>` tag. Unlike the
 * `nft:` marker, this file's bytes ARE encrypted — with the exact same AES key and
 * IV that encrypt the enclosing message (see `crypto.ts#decryptPayCashFile` and
 * `docs/LEARNINGS.md`), so we can decrypt and render it. We never emit this tag
 * ourselves (no upload endpoint of our own); we only parse and display it when a
 * PayCash-side contact sends one.
 */

/** Asset ids are uint64 — accept a plausible digit range only. */
const NFT_MARKER = /nft:(\d{6,20})/g

/** PayCash's own file-attachment tag: `<file>url;name;size;ext</file>`. */
const FILE_MARKER = /<file>([^<]*)<\/file>/g

export type MessagePart =
  | { kind: 'text'; text: string }
  | { kind: 'nft'; assetId: string }
  | { kind: 'file'; url: string; fileName: string; fileSize: number; fileExt: string }

export function formatNftMarker(assetId: string): string {
  return `nft:${String(assetId).trim()}`
}

/** How many characters an attachment costs against the message limit. */
export function nftMarkerLength(assetId: string): number {
  return formatNftMarker(assetId).length
}

function parseFileMarkerBody(body: string): MessagePart | null {
  // PayCash's own field order: url;fileName;fileSize;fileExt
  const [url, fileName, sizeStr, fileExt] = body.split(';')
  const fileSize = Number(sizeStr)
  if (!url || !fileName || !Number.isFinite(fileSize)) return null
  return { kind: 'file', url, fileName, fileSize, fileExt: fileExt ?? '' }
}

/** Splits message text into plain runs, NFT references, and PayCash file tags, preserving order. */
export function parseMessageParts(text: string): MessagePart[] {
  const src = String(text ?? '')
  const parts: MessagePart[] = []
  let lastIndex = 0

  type RawMatch = { start: number; end: number; part: MessagePart }
  const matches: RawMatch[] = []

  for (const match of src.matchAll(new RegExp(NFT_MARKER))) {
    const start = match.index ?? 0
    matches.push({ start, end: start + match[0].length, part: { kind: 'nft', assetId: match[1] } })
  }
  for (const match of src.matchAll(new RegExp(FILE_MARKER))) {
    const start = match.index ?? 0
    const part = parseFileMarkerBody(match[1])
    if (part) matches.push({ start, end: start + match[0].length, part })
  }
  matches.sort((a, b) => a.start - b.start)

  for (const m of matches) {
    if (m.start > lastIndex) {
      parts.push({ kind: 'text', text: src.slice(lastIndex, m.start) })
    }
    parts.push(m.part)
    lastIndex = m.end
  }
  if (lastIndex < src.length) parts.push({ kind: 'text', text: src.slice(lastIndex) })
  return parts
}

export function extractNftIds(text: string): string[] {
  return parseMessageParts(text)
    .filter((p): p is { kind: 'nft'; assetId: string } => p.kind === 'nft')
    .map(p => p.assetId)
}

/** True when the message is nothing but attachments (renders without a bubble tail). */
export function isAttachmentOnly(text: string): boolean {
  const parts = parseMessageParts(text)
  return (
    parts.length > 0 &&
    parts.every(p => (p.kind === 'text' ? p.text.trim().length === 0 : true))
  )
}
